import type { FC, ReactNode } from 'react';
import ContactIcon from '@untitled-ui/icons-react/build/esm/Send01';
import PhoneIcon from '@untitled-ui/icons-react/build/esm/Phone';
import { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Menu01Icon from '@untitled-ui/icons-react/build/esm/Menu01';
import XIcon from '@untitled-ui/icons-react/build/esm/X';
import type { Theme } from '@mui/material';
import {
  Box,
  Button,
  Collapse,
  Container,
  IconButton,
  Stack,
  SvgIcon,
  useMediaQuery,
  Typography
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SchoolIcon from '@mui/icons-material/School';
import { alpha } from '@mui/material/styles';
import { RouterLink } from 'src/components/router-link';
import { usePathname } from 'src/hooks/use-pathname';
import { useWindowScroll } from 'src/hooks/use-window-scroll';
import { paths } from 'src/paths';
import { useCompanySettings } from 'src/hooks/use-company-settings';

interface SubItem {
  title: string;
  path: string;
}

interface Item {
  disabled?: boolean;
  external?: boolean;
  popover?: ReactNode;
  path?: string;
  title: string;
  children?: SubItem[];
}

const items: Item[] = [
  {
    title: 'Home',
    path: paths.index
  },
  {
    title: 'About',
    path: paths.about.index
  },
  {
    title: 'Projects',
    path: paths.projects.caseStudies,
    children: [
      { title: 'Case Studies', path: paths.projects.caseStudies },
      { title: 'Recent Work', path: paths.projects.index }
    ]
  },
  {
    title: 'Services',
    path: paths.services.branding,
    children: [
      { title: 'Branding', path: paths.services.branding },
      { title: 'Websites', path: paths.services.websites },
      { title: 'Marketing', path: paths.services.marketing },
      { title: 'Google Business Profile', path: paths.services.googleBusinessProfile },
      { title: 'Ad Buying', path: paths.services.adBuying },
      { title: 'Content', path: paths.services.content },
      { title: 'AI Automation', path: paths.services.aiAutomation }
    ]
  },
  {
    title: 'Blog',
    path: paths.blog.index
  }
];

const TOP_NAV_HEIGHT: number = 64;

interface TopNavProps {
  onMobileNavOpen?: () => void;
}

export const TopNav: FC<TopNavProps> = (props) => {
  const pathname = usePathname();
  const { settings: co } = useCompanySettings();
  const companyFullName = co.fullCompanyName || co.companyName || '';
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const [elevate, setElevate] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [productsOpen, setProductsOpen] = useState<boolean>(false);
  const offset = 64;
  const delay = 100;

  const handleWindowScroll = useCallback(
    (): void => {
      if (window.scrollY > offset) {
        setElevate(true);
      } else {
        setElevate(false);
      }
    },
    []
  );

  const handleMobileMenuToggle = useCallback((): void => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const handleMobileMenuClose = useCallback((): void => {
    setMobileMenuOpen(false);
  }, []);

  const handleProductsToggle = useCallback((): void => {
    setProductsOpen((prev) => !prev);
  }, []);

  useWindowScroll({
    handler: handleWindowScroll,
    delay
  });

  // Close menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  return (
    <Box
      component="header"
      sx={{
        left: 0,
        position: 'fixed',
        right: 0,
        top: 0,
        pt: 2,
        zIndex: (theme) => theme.zIndex.appBar
      }}
    >
      <Container maxWidth="lg">
        {/* Unified Nav Container */}
        <Box
          sx={{
            backdropFilter: 'blur(12px)',
            backgroundColor: '#0a0a0b',
            borderRadius: mobileMenuOpen ? { xs: 0, md: 3 } : 2.5,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            // Full height on mobile when menu is open
            ...(mobileMenuOpen && {
              position: { xs: 'fixed', md: 'relative' },
              top: { xs: 0, md: 'auto' },
              left: { xs: 0, md: 'auto' },
              right: { xs: 0, md: 'auto' },
              bottom: { xs: 0, md: 'auto' },
              height: { xs: '100vh', md: 'auto' },
              display: 'flex',
              flexDirection: 'column'
            })
          }}
        >
          {/* Top Bar */}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              height: TOP_NAV_HEIGHT,
              px: 2
            }}
          >
            <Stack
              alignItems="center"
              direction="row"
              spacing={1}
              sx={{ flexGrow: 1 }}
            >
              <Stack
                alignItems="center"
                component={RouterLink}
                direction="row"
                display="inline-flex"
                href={paths.index}
                spacing={1}
                sx={{
                  textDecoration: 'none',
                  flexShrink: 1,
                  minWidth: 0,
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    height: { xs: 32, sm: 32, md: 34 },
                    width: 'auto'
                  }}
                >
                  <img
                    src="/assets/logo-main.png"
                    alt="Mystic Media Film"
                    style={{ height: '100%', width: 'auto', objectFit: 'contain' }}
                  />
                </Box>
                <Box
                  sx={{
                    color: '#ffffff',
                    fontFamily: '\'Plus Jakarta Sans\', sans-serif',
                    fontSize: { xs: 16, sm: 13, md: 17.2 },
                    fontWeight: 800,
                    letterSpacing: '0.3px',
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: { xs: 'none', sm: 'block' },
                    transition: 'color 0.3s ease',
                    '& .tagline': {
                      color: 'primary.main',
                      display: { xs: 'none', sm: 'none', md: 'inline' }
                    }
                  }}
                >
                  Mystic Media Film
                </Box>
              </Stack>
            </Stack>
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="flex-end"
              spacing={2}
              sx={{ flexGrow: 1 }}
            >
<IconButton
                onClick={handleMobileMenuToggle}
                sx={{
                  color: '#ffffff',
                  transition: 'transform 0.2s ease, color 0.3s ease',
                  transform: mobileMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)'
                }}
              >
                <SvgIcon>
                  {mobileMenuOpen ? <XIcon /> : <Menu01Icon />}
                </SvgIcon>
              </IconButton>
            </Stack>
          </Stack>

          {/* Dropdown Content - Integrated */}
          <Collapse
            in={mobileMenuOpen}
            timeout={{ enter: 400, exit: 300 }}
            easing={{ enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
            sx={{
              flex: { xs: 1, md: 'none' },
              minHeight: 0, // Allow flex shrinking
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              '& .MuiCollapse-wrapper': {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
              },
              '& .MuiCollapse-wrapperInner': {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0
              }
            }}
          >
            <Box
              sx={{
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                flex: { xs: 1, md: 'none' },
                maxHeight: { xs: 'calc(100vh - 64px)', md: 'calc(100vh - 140px)' },
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch', // Enable smooth scrolling on iOS
                background: 'linear-gradient(180deg, rgba(15, 15, 17, 0) 0%, rgba(15, 15, 17, 0.5) 100%)',
                display: 'flex',
                flexDirection: 'column',
                opacity: mobileMenuOpen ? 1 : 0,
                transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(-10px)',
                transition: 'opacity 0.3s ease 0.1s, transform 0.3s ease 0.1s'
              }}
            >
              {/* Four Column Layout */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 0,
                  py: 1,
                  pb: { xs: 3, md: 1 }, // Extra padding at bottom for mobile scrolling
                  flexShrink: 0
                }}
              >
                {/* Column 1: Main Pages */}
                <Box sx={{ p: 2.5, borderRight: { sm: '1px solid rgba(255, 255, 255, 0.06)' } }}>
                  <Typography
                    sx={{
                      px: 1.5,
                      pb: 2,
                      color: '#2970FF',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em'
                    }}
                  >
                    Main Pages
                  </Typography>
                  <Stack spacing={0.5}>
                    {items.map((item) => (
                      item.children ? (
                        <Box key={item.title}>
                          <Box
                            onClick={handleProductsToggle}
                            sx={{
                              py: 1.25,
                              px: 1.5,
                              borderRadius: 2,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(41, 112, 255, 0.08)'
                              }
                            }}
                          >
                            <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.9rem' }}>
                              {item.title}
                            </Typography>
                            {productsOpen ? (
                              <ExpandLess sx={{ color: '#2970FF', fontSize: 20 }} />
                            ) : (
                              <ExpandMore sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
                            )}
                          </Box>
                          <Collapse in={productsOpen}
timeout="auto"
unmountOnExit>
                            <Stack spacing={0.25}
sx={{ pl: 2, pt: 0.5 }}>
                              {item.children.map((child) => (
                                <Box
                                  key={child.title}
                                  component={RouterLink}
                                  href={child.path}
                                  onClick={handleMobileMenuClose}
                                  sx={{
                                    py: 1,
                                    px: 1.5,
                                    borderRadius: 1.5,
                                    textDecoration: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    transition: 'all 0.2s ease',
                                    borderLeft: pathname === child.path
                                      ? '2px solid #2970FF'
                                      : '2px solid transparent',
                                    backgroundColor: pathname === child.path
                                      ? 'rgba(41, 112, 255, 0.1)'
                                      : 'transparent',
                                    '&:hover': {
                                      backgroundColor: 'rgba(41, 112, 255, 0.08)',
                                      borderLeftColor: 'rgba(41, 112, 255, 0.5)'
                                    }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      backgroundColor: pathname === child.path ? '#2970FF' : 'rgba(255,255,255,0.3)'
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      color: pathname === child.path ? '#2970FF' : 'rgba(255,255,255,0.7)',
                                      fontWeight: pathname === child.path ? 600 : 400,
                                      fontSize: '0.85rem'
                                    }}
                                  >
                                    {child.title}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Collapse>
                        </Box>
                      ) : (
                        <Box
                          key={item.title}
                          component={RouterLink}
                          href={item.path || '#'}
                          onClick={handleMobileMenuClose}
                          sx={{
                            py: 1.25,
                            px: 1.5,
                            borderRadius: 2,
                            textDecoration: 'none',
                            display: 'block',
                            transition: 'all 0.2s ease',
                            backgroundColor: pathname === item.path
                              ? 'rgba(41, 112, 255, 0.1)'
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(41, 112, 255, 0.08)'
                            }
                          }}
                        >
                          <Typography
                            sx={{
                              color: pathname === item.path ? '#2970FF' : '#ffffff',
                              fontWeight: pathname === item.path ? 600 : 500,
                              fontSize: '0.9rem'
                            }}
                          >
                            {item.title}
                          </Typography>
                        </Box>
                      )
                    ))}
                  </Stack>
                </Box>

{/* Column 4: Support */}
                <Box sx={{ p: 2.5 }}>
                  <Typography
                    sx={{
                      px: 1.5,
                      pb: 2,
                      color: '#2970FF',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em'
                    }}
                  >
                    Support
                  </Typography>
                  <Stack spacing={0.5}>
                    {([
                      { title: 'Contact Support', path: paths.support, external: false },
                      { title: 'Schedule', path: paths.schedule, external: false },
                      { title: 'Creator Terminal', path: 'https://www.creatorterminal.com/', external: true }
                    ] as Array<{ title: string; path: string; external: boolean }>).map((item) => (
                      <Box
                        key={item.title}
                        component={item.external ? 'a' : RouterLink}
                        href={item.path}
                        {...(item.external && { target: '_blank', rel: 'noopener noreferrer' })}
                        onClick={handleMobileMenuClose}
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          borderRadius: 2,
                          textDecoration: 'none',
                          display: 'block',
                          transition: 'all 0.2s ease',
                          backgroundColor: pathname === item.path
                            ? 'rgba(41, 112, 255, 0.1)'
                            : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(41, 112, 255, 0.08)'
                          }
                        }}
                      >
                        <Typography
                          sx={{
                            color: pathname === item.path ? '#2970FF' : '#ffffff',
                            fontWeight: pathname === item.path ? 600 : 500,
                            fontSize: '0.9rem'
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>

              {/* Bottom CTA Bar */}
              <Box
                sx={{
                  p: 2.5,
                  borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                  background: 'linear-gradient(180deg, rgba(41, 112, 255, 0.03) 0%, rgba(41, 112, 255, 0.08) 100%)',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: 'auto'
                }}
              >
                <Button
                  component={RouterLink}
                  href="tel:+18138150142"
                  variant="contained"
                  startIcon={
                    <SvgIcon sx={{ fontSize: 18 }}>
                      <PhoneIcon />
                    </SvgIcon>
                  }
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    borderRadius: 2.5,
                    backgroundColor: '#2970FF',
                    boxShadow: '0 4px 14px rgba(41, 112, 255, 0.4)',
                    '&:hover': {
                      backgroundColor: '#1e5cd9',
                      boxShadow: '0 6px 20px rgba(41, 112, 255, 0.5)',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  (813) 815-0142
                </Button>
                <Button
                  component="a"
                  href="mailto:sales@mysticmediafilm.com"
                  variant="outlined"
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    borderRadius: 2.5,
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    borderWidth: 2,
                    color: '#ffffff',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#2563eb',
                      backgroundColor: 'rgba(37, 99, 235, 0.1)',
                      borderWidth: 2
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  sales@mysticmediafilm.com
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Container>
    </Box>
  );
};

TopNav.propTypes = {
  onMobileNavOpen: PropTypes.func
};
